import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import "./websiteBuilderColorPicker.scss";
import { faPalette } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  IWebsiteBuilderColorPickerPopupPosition,
  WEBSITE_BUILDER_COLOR_PICKER_POPUP_MARGIN,
  WEBSITE_BUILDER_COLOR_PICKER_POPUP_TOTAL_HEIGHT,
  WebsiteBuilderColorPickerPopup,
} from "./popup/WebsiteBuilderColorPickerPopup";

interface IWebsiteBuilderColorPickerProps {
  title: string;
  hexColor: string;
  updateHexColor: (updatedHexColor: string) => void;
}

interface IWebsiteBuilderColorPickerPopupState {
  open: boolean;
  position: IWebsiteBuilderColorPickerPopupPosition;
}

const initialPopupState: IWebsiteBuilderColorPickerPopupState = {
  open: false,
  position: null,
};

export function WebsiteBuilderColorPicker(props: IWebsiteBuilderColorPickerProps) {
  const { title, hexColor, updateHexColor } = props;

  const colorPickerRef = useRef<HTMLDivElement>(null);
  const colorPickerPopupRef = useRef<HTMLDivElement>(null);

  const [popupState, setPopupState] = useState<IWebsiteBuilderColorPickerPopupState>({ ...initialPopupState });

  function closePopup() {
    setPopupState({ ...initialPopupState });
  }

  const handlePointerDownOutsideColorPicker = useCallback((event: PointerEvent) => {
    if (
      colorPickerRef.current &&
      colorPickerPopupRef.current &&
      event.target instanceof Node &&
      !colorPickerRef.current.contains(event.target) &&
      !colorPickerPopupRef.current.contains(event.target)
    ) {
      closePopup();
    }
  }, []);

  useEffect(() => {
    if (popupState.open) {
      document.addEventListener("pointerdown", handlePointerDownOutsideColorPicker, { capture: true });
    }

    return () => {
      document.removeEventListener("pointerdown", handlePointerDownOutsideColorPicker, { capture: true });
    };
  }, [popupState.open, handlePointerDownOutsideColorPicker]);

  function togglePopupState() {
    const colorPickerDimensions = colorPickerRef.current?.getBoundingClientRect();

    if (popupState.open || !colorPickerDimensions) {
      closePopup();
    } else {
      const x = colorPickerDimensions.x + colorPickerDimensions.width / 2;
      let y = colorPickerDimensions.y - WEBSITE_BUILDER_COLOR_PICKER_POPUP_TOTAL_HEIGHT;

      if (y < 0) {
        y = colorPickerDimensions.y + colorPickerDimensions.height + WEBSITE_BUILDER_COLOR_PICKER_POPUP_MARGIN;
      }

      setPopupState(prev => ({
        ...prev,
        open: true,
        position: {
          x,
          y,
        },
      }));
    }
  }

  return (
    <div
      ref={colorPickerRef}
      className={classNames("website-builder-color-picker", {
        "color-picker-popup-open": popupState.open,
      })}
      onPointerDown={togglePopupState}
    >
      {popupState.open && (
        <WebsiteBuilderColorPickerPopup
          ref={colorPickerPopupRef}
          position={popupState.position}
          hexColor={hexColor}
          updateHexColor={updateHexColor}
          closePopup={closePopup}
        />
      )}
      <div className="color-picker-color-container">
        <div
          className="color-picker-color"
          style={{
            backgroundColor: hexColor,
          }}
        >
          {hexColor == null && <div className="color-picker-color-slash" />}
        </div>
        <div className="color-picker-title">{title}</div>
      </div>
      <FontAwesomeIcon className="color-picker-palette-icon" icon={faPalette} />
    </div>
  );
}
